import type { ReactNode } from "react";
import { Download, SlidersHorizontal } from "@/components/icons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  DatePicker,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import type {
  ProfitDateBasis,
  ReportDateBasis,
} from "@/modules/reports/domain/constants";
import type { ReportProductFilterDTO } from "@/modules/reports/domain/contracts";
import {
  buildCustomDateRange,
  type ReportDatePreset,
  type ReportDateRangeState,
  reportRangeToInclusiveEnd,
  reportRangeToPickerValue,
} from "@/modules/reports/domain/date-range-presets";
import {
  isSavedViewActive,
  REPORTS_SAVED_VIEWS,
  type ReportsSavedView,
} from "../lib/reports-saved-views";
import type { ReportsTab } from "../lib/reports-search";
import { ReportProductVariantFilter } from "./ReportProductVariantFilter";

const PRESETS: Array<{ id: ReportDatePreset; label: string }> = [
  { id: "today", label: "ມື້ນີ້" },
  { id: "7d", label: "7 ວັນ" },
  { id: "month", label: "ເດືອນນີ້" },
  { id: "custom", label: "ກຳນົດເອງ" },
];

const BASIS_OPTIONS: Array<{ id: ReportDateBasis; label: string }> = [
  { id: "created", label: "ວັນສ້າງ" },
  { id: "shipped", label: "ວັນສົ່ງ" },
];

const PROFIT_BASIS_OPTIONS: Array<{ id: ProfitDateBasis; label: string }> = [
  { id: "paid_at", label: "ວັນຊຳລະ" },
  { id: "created_at", label: "ວັນສ້າງ" },
];

type ReportsFilterBarProps = {
  showDateRange: boolean;
  showSalesBasis: boolean;
  showProfitBasis: boolean;
  showMoreFilters: boolean;
  range: ReportDateRangeState;
  activePreset: ReportDatePreset;
  profitBasis: ProfitDateBasis;
  productFilter: ReportProductFilterDTO;
  moreFiltersOpen: boolean;
  moreFiltersActive?: boolean;
  moreFilters?: ReactNode;
  exportAvailable: boolean;
  exportLabel: string;
  exporting?: boolean;
  onPresetChange: (preset: ReportDatePreset) => void;
  onBasisChange: (basis: ReportDateBasis) => void;
  onProfitBasisChange: (basis: ProfitDateBasis) => void;
  onCustomRangeChange: (range: ReportDateRangeState) => void;
  onProductFilterChange: (next: ReportProductFilterDTO) => void;
  onMoreFiltersOpenChange: (open: boolean) => void;
  onExport: () => void;
  activeTab: ReportsTab;
  activeSection: string;
  onSavedViewSelect: (view: ReportsSavedView) => void;
};

export function ReportsFilterBar({
  showDateRange,
  showSalesBasis,
  showProfitBasis,
  showMoreFilters,
  range,
  activePreset,
  profitBasis,
  productFilter,
  moreFiltersOpen,
  moreFiltersActive,
  moreFilters,
  exportAvailable,
  exportLabel,
  exporting,
  onPresetChange,
  onBasisChange,
  onProfitBasisChange,
  onCustomRangeChange,
  onProductFilterChange,
  onMoreFiltersOpenChange,
  onExport,
  activeTab,
  activeSection,
  onSavedViewSelect,
}: ReportsFilterBarProps) {
  const pickerValue = reportRangeToPickerValue(range);
  const fromLabel = new Date(range.from).toLocaleDateString("lo-LA");
  const toLabel = reportRangeToInclusiveEnd(range.to).toLocaleDateString(
    "lo-LA",
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="me-1 text-muted-foreground text-xs">ມຸມມອງ</span>
        {REPORTS_SAVED_VIEWS.map((view) => {
          const active = isSavedViewActive(view, {
            tab: activeTab,
            section: activeSection,
            preset: activePreset,
          });
          return (
            <button
              key={view.id}
              type="button"
              onClick={() => onSavedViewSelect(view)}
              className={cn(
                "rounded-full border px-2.5 py-1 font-medium text-xs transition-colors",
                active
                  ? "border-secondary bg-secondary/15 text-secondary"
                  : "border-border bg-background text-foreground hover:bg-muted/60",
              )}
            >
              {view.label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-col gap-2 rounded-2xl border border-secondary/15 bg-background p-3 sm:gap-3 sm:p-4 md:sticky md:top-0 md:z-10 md:bg-background/95 md:shadow-sm md:backdrop-blur md:supports-backdrop-filter:bg-background/85">
        {showDateRange ? (
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onPresetChange(p.id)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 font-medium text-xs transition-colors sm:px-3 sm:py-1.5 sm:text-sm",
                    activePreset === p.id
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background text-foreground hover:bg-muted/60",
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {activePreset === "custom" ? (
              <DatePicker
                mode="range"
                className="w-full rounded-full sm:max-w-md"
                placeholder="ເລືອກວັນເລີ່ມ – ວັນສິ້ນສຸດ"
                captionLayout="dropdown"
                fromYear={2024}
                toYear={new Date().getFullYear() + 1}
                value={pickerValue}
                onChange={(selected) => {
                  if (!selected?.from || !selected?.to) return;
                  onCustomRangeChange(
                    buildCustomDateRange(
                      selected.from,
                      selected.to,
                      range.basis,
                    ),
                  );
                }}
              />
            ) : (
              <p className="text-muted-foreground text-xs">
                {fromLabel} – {toLabel}
              </p>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            ສ່ວນນີ້ໃຊ້ເກນອາຍຸ lot / ການຕັ້ງຄ່າສິນຄ້າຄ້າງ — ບໍ່ໃຊ້ຊ່ວງວັນ
          </p>
        )}

        <div className="flex flex-col gap-2 sm:gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:gap-3">
            {showDateRange && showSalesBasis ? (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs">ນັບຕາມ</span>
                <div className="inline-flex gap-1 rounded-full border border-border bg-muted/30 p-1">
                  {BASIS_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onBasisChange(opt.id)}
                      className={cn(
                        "rounded-full px-2.5 py-1 font-medium text-xs transition-colors sm:px-3 sm:py-1.5 sm:text-sm",
                        range.basis === opt.id
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {showProfitBasis ? (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs">ນັບກຳໄລຕາມ</span>
                <div className="inline-flex gap-1 rounded-full border border-border bg-muted/30 p-1">
                  {PROFIT_BASIS_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onProfitBasisChange(opt.id)}
                      className={cn(
                        "rounded-full px-2.5 py-1 font-medium text-xs transition-colors sm:px-3 sm:py-1.5 sm:text-sm",
                        profitBasis === opt.id
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="min-w-0 flex-1 sm:max-w-xl">
              <ReportProductVariantFilter
                value={productFilter}
                onChange={onProductFilterChange}
                compact
              />
            </div>
          </div>

          {exportAvailable ? (
            <div className="flex shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={exporting}
                onClick={onExport}
              >
                <Download data-icon="inline-start" />
                {exportLabel}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {showMoreFilters ? (
        <Accordion
          type="single"
          collapsible
          value={moreFiltersOpen ? "more" : ""}
          onValueChange={(value) => onMoreFiltersOpenChange(value === "more")}
          className="rounded-2xl border border-secondary/15 bg-background px-3 sm:px-4"
        >
          <AccordionItem value="more" className="border-0">
            <AccordionTrigger
              className={cn(
                "py-3 hover:no-underline sm:py-3.5",
                moreFiltersActive && "text-secondary",
              )}
            >
              <span className="flex items-center gap-2">
                <SlidersHorizontal className="size-4 shrink-0" />
                ຕົວກອງເພີ່ມ
                {moreFiltersActive ? (
                  <span className="rounded-full bg-secondary/15 px-2 py-0.5 font-medium text-secondary text-xs">
                    ໃຊ້ຢູ່
                  </span>
                ) : null}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 sm:pb-4">
              {moreFilters}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : null}
    </div>
  );
}
